import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FileText,
  CheckCircle,
  Clock,
  Eye,
  Edit,
  LogOut,
  Calendar,
  FileDown,
  ExternalLink,
  XCircle,
  Settings,
  ChevronDown,
  ChevronUp,
  Bell,
  X,
  Fuel,
  Truck
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useHourmeters } from '../../hooks/useHourmeters';
import { useDumperHourmeters } from '../../hooks/useDumperHourmeters';
import { useFuelDiscounts } from '../../hooks/useFuelDiscounts';
import { useActiveContext } from '../../hooks/useActiveContext';
import { hourmeterService } from '../../services/api';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import ResidentSessionIndicator from '../../components/ResidentSessionIndicator';
import { cn, formatDate, formatHours, getHoursWorked, generateGroupName } from '../../utils';
import type { Hourmeter } from '../../types';
import { Plus } from 'lucide-react';

type StatusFilter = 'all' | 'signed' | 'pending' | 'rejected';

const Dashboard: React.FC = () => {
  const { operator, logout } = useAuth();
  const { hourmeters, hourmeterGroups, loading, error, refetch } = useHourmeters();
  const { trips: dumperTrips } = useDumperHourmeters();
  const { stats: fuelStats } = useFuelDiscounts();
  const { context: activeContext } = useActiveContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState<StatusFilter>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [reminderModal, setReminderModal] = useState<{ type: 'success' | 'already_sent' | 'error'; message: string } | null>(null);
  const [sendingReminder, setSendingReminder] = useState<number | null>(null);

  // Refetch data when returning to dashboard (e.g., after signing a receipt)
  useEffect(() => {
    if (location.state?.shouldRefresh) {
      refetch();
      // Clear the state to avoid unnecessary refetches
      navigate('/dashboard', { replace: true, state: {} });
    }
  }, [location.state, refetch, navigate]);

  const handleViewReceipt = (hourmeter: Hourmeter) => {
    navigate(`/receipt/${hourmeter.id}`, { 
      state: { hourmeter } 
    });
  };

  const handleSignReceipt = (hourmeter: Hourmeter) => {
    navigate(`/receipt/${hourmeter.id}?mode=sign`, { 
      state: { hourmeter } 
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleViewPDF = (hourmeter: Hourmeter) => {
    if (!operator) return;
    const userRole = operator.role || 'operator';
    const pdfUrl = `${((import.meta as any).env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.hostname === 'localhost' ? 'http://localhost:2000' : window.location.hostname.includes('stg-') ? 'https://stg-retri-b90a7a43a76f.herokuapp.com' : 'https://retriapp.herokuapp.com' : 'http://localhost:2000'))}/api/v2/platform/operation-dashboard/hourmeters/${hourmeter.id}/pdf?operatorId=${operator.id}&download=false&role=${userRole}`;
    window.open(pdfUrl, '_blank');
  };

  const handleDownloadPDF = (hourmeter: Hourmeter) => {
    if (!operator) return;
    const userRole = operator.role || 'operator';
    const downloadUrl = `${((import.meta as any).env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.hostname === 'localhost' ? 'http://localhost:2000' : window.location.hostname.includes('stg-') ? 'https://stg-retri-b90a7a43a76f.herokuapp.com' : 'https://retriapp.herokuapp.com' : 'http://localhost:2000'))}/api/v2/platform/operation-dashboard/hourmeters/${hourmeter.id}/pdf?operatorId=${operator.id}&download=true&role=${userRole}`;
    window.open(downloadUrl, '_blank');
  };

  const handleEditHourmeter = (hourmeter: Hourmeter) => {
    navigate(`/edit-hourmeter/${hourmeter.id}`, {
      state: { hourmeter }
    });
  };

  const handleFilterClick = (filter: StatusFilter) => {
    setActiveFilter(prev => prev === filter ? 'all' : filter);
  };

  const toggleGroup = (id: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isOlderThanOneDay = (date: string) => {
    const hourmeterDate = new Date(date)
    const now = new Date()
    const diffMs = now.getTime() - hourmeterDate.getTime()
    return diffMs > 24 * 60 * 60 * 1000
  }

  const handleSendReminder = async (hourmeter: Hourmeter, groupResidentId?: number | null) => {
    const residentId = hourmeter.residentId || groupResidentId
    if (!operator || !residentId) return
    setSendingReminder(hourmeter.id)
    try {
      const response = await hourmeterService.sendReminderToResident(operator.id, residentId)
      setReminderModal({ type: 'success', message: response.message || 'Recordatorio enviado exitosamente' })
    } catch (err: any) {
      const status = err?.response?.status
      const message = err?.response?.data?.message || 'Error al enviar el recordatorio'
      if (status === 409) {
        setReminderModal({ type: 'already_sent', message })
      } else {
        setReminderModal({ type: 'error', message })
      }
    } finally {
      setSendingReminder(null)
    }
  }

  const filteredGroups = hourmeterGroups.map(group => ({
    ...group,
    hourmeters: group.hourmeters.filter(h => {
      if (activeFilter === 'signed') return h.isSigned;
      if (activeFilter === 'rejected') return h.isRejected;
      if (activeFilter === 'pending') return !h.isSigned && !h.isRejected;
      return true;
    }),
  })).filter(group => group.hourmeters.length > 0);

  // Viajes de volqueta agrupados por condición (grupo de condiciones) + centro
  // de costo (deal/operation). RS-1338: solo aplica a viajes; los horómetros
  // genéricos siguen agrupados por costCenterId.
  const dumperTripGroups = useMemo(() => {
    const map = new Map<string, { label: string; trips: typeof dumperTrips }>();
    for (const t of dumperTrips) {
      const ccKey = t.dealId != null ? `D:${t.dealId}` : `O:${t.operationId ?? 0}`;
      const gKey = t.conditionGroupId != null ? `G:${t.conditionGroupId}` : 'G:-';
      const key = `${ccKey}|${gKey}`;
      const ccLabel = t.dealId != null ? `Negocio #${t.dealId}` : `Operación #${t.operationId ?? 0}`;
      const label = t.groupName ? `${t.groupName} · ${ccLabel}` : ccLabel;
      const entry = map.get(key) || { label, trips: [] as typeof dumperTrips };
      entry.trips.push(t);
      map.set(key, entry);
    }
    return Array.from(map.values());
  }, [dumperTrips]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200">
        <Loading size="lg" text="Cargando horometros..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Header */}
      <header className="bg-retri-blue shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0">
                <img
                  src="/logo-retri-blue.png"
                  alt="RETRI Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-xl font-bold text-white truncate">RETRI</h1>
                <p className="text-xs sm:text-sm text-blue-200 truncate">Registro de ejecución</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4 ml-2">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{operator?.name}</p>
                <p className="text-xs text-blue-200">
                  {operator?.role === 'resident'
                    ? 'Residente'
                    : operator?.role === 'supervisor'
                      ? 'Supervisor'
                      : (operator?.operatorRole === 'DRIVER' || activeContext?.operatorRole === 'DRIVER')
                        ? 'Transportista'
                        : 'Operador'}
                </p>
              </div>
              <div className="text-right block sm:hidden">
                <p className="text-xs font-medium text-white truncate max-w-20">{operator?.name}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                title="Cerrar sesión"
                className="flex-shrink-0 text-white hover:bg-retri-bluedark"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-6">
          {/* Welcome Section */}
          {(() => {
            const isDriverCtx =
              operator?.operatorRole === 'DRIVER' ||
              (activeContext && activeContext.kind !== 'NONE' && activeContext.operatorRole === 'DRIVER')
            const dumperPlates: string[] = (() => {
              if (!activeContext || activeContext.kind === 'NONE') return []
              const fromAssets = (activeContext.transportAssets ?? [])
                .map(a => a.licensePlate)
                .filter((p): p is string => !!p)
              if (fromAssets.length > 0) return Array.from(new Set(fromAssets))
              return activeContext.licensePlate ? [activeContext.licensePlate] : []
            })()
            const welcomeText = operator?.role === 'resident'
              ? 'Aquí puedes ver los horometros asignados y firmarlos directamente'
              : operator?.role === 'supervisor'
                ? 'Aquí puedes ver los horometros del proyecto asignado para hacer seguimiento'
                : isDriverCtx
                  ? 'Aquí puedes ver tus viajes detectados y solicitar la firma del residente'
                  : 'Aquí puedes ver tus horometros registrados y solicitar firma del residente'
            return (
              <div className="text-center">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2">
                  Bienvenido, {operator?.name}
                </h2>
                <p className="text-sm sm:text-base text-gray-600">{welcomeText}</p>
                {isDriverCtx && dumperPlates.length > 0 && (
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {dumperPlates.map(plate => (
                      <span
                        key={plate}
                        className="inline-flex items-center gap-1.5 rounded-md border border-retri-blue/20 bg-retri-blue/5 px-2.5 py-1 text-sm font-semibold text-retri-blue"
                      >
                        <Truck className="h-4 w-4" />
                        {plate}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}
          
          {/* Supervisor Session Indicator - Mobile */}
          <div className="block sm:hidden">
            <ResidentSessionIndicator />
          </div>

          {error && (
            <Alert variant="error" title="Error">
              {error}
              <Button
                variant="outline"
                size="sm"
                onClick={refetch}
                className="mt-2"
              >
                Reintentar
              </Button>
            </Alert>
          )}

          {/* Supervisor Session Indicator - Desktop */}
          <div className="hidden sm:block">
            <ResidentSessionIndicator />
          </div>

          {/* Stats Cards */}
          {(() => {
            const isResident = operator?.role === 'resident';
            const showFuel = !isResident;
            const dumperSigned = dumperTrips.filter(t => t.status === 'FIRMADO' || t.status === 'REGISTRADO').length
            const dumperPending = dumperTrips.filter(t => t.status === 'PENDIENTE').length
            const dumperRejected = dumperTrips.filter(t => t.status === 'RECHAZADO').length
            const statCards = [
              {
                filter: 'all' as StatusFilter,
                icon: <FileText className="h-6 w-6 text-retri-blue" />,
                label: 'Total',
                count: hourmeters.length + dumperTrips.length,
                ring: 'ring-retri-blue',
              },
              {
                filter: 'signed' as StatusFilter,
                icon: <CheckCircle className="h-6 w-6 text-retri-green" />,
                label: 'Firmados',
                count: hourmeters.filter(h => h.isSigned).length + dumperSigned,
                ring: 'ring-retri-green',
              },
              {
                filter: 'pending' as StatusFilter,
                icon: <Clock className="h-6 w-6 text-retri-orange" />,
                label: 'Pend.',
                count: hourmeters.filter(h => !h.isSigned && !h.isRejected).length + dumperPending,
                ring: 'ring-retri-orange',
              },
              {
                filter: 'rejected' as StatusFilter,
                icon: <XCircle className="h-6 w-6 text-red-500" />,
                label: 'Rech.',
                count: hourmeters.filter(h => h.isRejected).length + dumperRejected,
                ring: 'ring-red-500',
              },
            ];
            return (
              <div className={cn('grid gap-2', showFuel ? 'grid-cols-5' : 'grid-cols-4')}>
                {statCards.map(({ filter, icon, label, count, ring }) => {
                  const isActive = activeFilter === filter;
                  return (
                    <Card
                      key={filter}
                      className={cn(
                        'transition-all',
                        isResident && 'cursor-pointer hover:shadow-md',
                        isActive && isResident && `ring-2 ${ring}`
                      )}
                      onClick={isResident ? () => handleFilterClick(filter) : undefined}
                    >
                      <CardContent className="p-3 flex flex-col items-center justify-center gap-1">
                        {icon}
                        <p className="text-xs text-gray-500 leading-none">{label}</p>
                        <p className="text-xl font-bold text-gray-900 leading-none">{count}</p>
                      </CardContent>
                    </Card>
                  );
                })}
                {showFuel ? (
                  <Card
                    className="transition-all cursor-pointer hover:shadow-md"
                    onClick={() => navigate('/fuel-loads')}
                  >
                    <CardContent className="p-3 flex flex-col items-center justify-center gap-1">
                      <Fuel className="h-6 w-6 text-retri-orange" />
                      <p className="text-xs text-gray-500 leading-none">Comb.</p>
                      <p className="text-xl font-bold text-gray-900 leading-none">
                        {fuelStats.totalLoads}
                      </p>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            );
          })()}

          {operator && operator.role !== 'resident' && operator.role !== 'supervisor' && (() => {
            // DRIVER siempre opera volqueta. Label + ruta van a flujo dumper,
            // sin importar si activeContext.isDumper resolvió correctamente.
            const isDriver = operator?.operatorRole === 'DRIVER'
              || (activeContext && activeContext.kind !== 'NONE' && activeContext.operatorRole === 'DRIVER')
            const isDumperFlow = isDriver
              || (activeContext?.kind === 'OPERATION' && activeContext.isDumper)
              || (activeContext?.kind === 'DEAL' && activeContext.isDumper)
            return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                variant="primary"
                size="default"
                onClick={() => {
                  if (!activeContext || activeContext.kind === 'NONE') {
                    alert(activeContext?.reason || 'Sin asignación activa, contacta a tu coordinador')
                    return
                  }
                  // RS-1338: N tarifas para la misma volqueta → el transportista
                  // elige antes de abrir el form. (activeContext ya es non-NONE
                  // por el guard de arriba.)
                  const opts = activeContext.transportAssets ?? []
                  if (isDumperFlow && opts.length > 1) {
                    navigate('/dumper-hourmeters/select-asset', { state: { assets: opts } })
                  } else if (activeContext.kind === 'OPERATION' && isDumperFlow) {
                    const tid = activeContext.transportAssetId ?? activeContext.assetId
                    navigate(`/dumper-hourmeters/register?operationId=${activeContext.operationId}&transportAssetId=${tid}`)
                  } else if (activeContext.kind === 'OPERATION') {
                    navigate(`/create-hourmeter?operationId=${activeContext.operationId}`)
                  } else if (activeContext.kind === 'DEAL' && isDumperFlow) {
                    const legOp = (activeContext as any).legacyOperatorId ?? ''
                    navigate(`/dumper-hourmeters/register?dealId=${activeContext.dealId}&legacyOperatorId=${legOp}`)
                  } else {
                    navigate('/create-hourmeter')
                  }
                }}
                className="w-full justify-center h-12"
              >
                {isDumperFlow ? (
                  <>
                    <Truck className="w-4 h-4 mr-2" />
                    Crear recibo
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Horómetro
                  </>
                )}
              </Button>
              <Button
                size="default"
                onClick={() => {
                  // Volqueta: usa módulo machinery_expenses RS-1338; el resto va a legacy fuel-loads
                  navigate(isDumperFlow ? '/dumper-fuel/register' : '/fuel-loads')
                }}
                className="w-full justify-center h-12 bg-retri-orange text-white hover:bg-[#d6860f] font-semibold"
              >
                <Fuel className="w-4 h-4 mr-2" />
                Combustible
              </Button>
            </div>
            )
          })()}
          {/* Escanear volqueta — solo residente. El listado de viajes vive debajo
              (sección "Mis viajes de volqueta"), por eso ya no hay botón redundante. */}
          {operator?.role === 'resident' && (
            <Button
              size="default"
              onClick={() => navigate('/dumper-hourmeters/qr')}
              className="w-full justify-center h-12 bg-retri-green text-white hover:opacity-90 font-semibold"
            >
              <Truck className="w-4 h-4 mr-2" />
              Escanear volqueta
            </Button>
          )}
          {/* Sección viajes de volqueta — visible si hay trips registrados.
              El listado completo /dumper-hourmeters fue eliminado: el dashboard
              muestra los últimos 5 directamente y se accede al detalle por click. */}
          {dumperTrips.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Truck className="w-4 h-4" /> Mis viajes de volqueta
                </h2>
                <span className="text-xs text-gray-500">{dumperTrips.length} total</span>
              </div>
              <div className="space-y-3">
                {dumperTripGroups.map((g) => (
                  <div key={g.label} className="space-y-1">
                    <p className="text-xs font-medium text-gray-500">{g.label}</p>
                    {g.trips.slice(0, 5).map((t) => (
                      <Card
                        key={t.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/dumper-hourmeters/${t.id}`)}
                      >
                        <CardContent className="p-3 flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">
                              Remisión {t.remisionNumber}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(t.tripDate)} · {t.realM3} m³
                              {t.distanceKm ? ` · ${t.distanceKm} km` : ''}
                            </div>
                          </div>
                          <span className={cn(
                            'text-[10px] font-medium px-2 py-1 rounded',
                            t.status === 'FIRMADO' && 'bg-green-100 text-green-700',
                            t.status === 'PENDIENTE' && 'bg-orange-100 text-orange-700',
                            t.status === 'REGISTRADO' && 'bg-blue-100 text-blue-700',
                            t.status === 'RECHAZADO' && 'bg-red-100 text-red-700',
                            t.status === 'EDITADO' && 'bg-gray-100 text-gray-600',
                          )}>
                            {t.status}
                          </span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mobile View */}
          {hourmeterGroups.length === 0 && dumperTrips.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <FileText className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
              <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">
                No hay horometros registrados
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Cuando se registren horometros aparecerán aquí
              </p>
            </div>
          ) : hourmeterGroups.length === 0 ? null : filteredGroups.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <FileText className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
              <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">
                Sin resultados para este filtro
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                No hay horometros que coincidan con el filtro seleccionado
              </p>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
                  <div className="block md:hidden space-y-6">
                    {filteredGroups.map((group) => {
                      const isCollapsed = collapsedGroups.has(group.costCenterId);
                      return (
                      <div key={group.costCenterId} className="space-y-3">
                        {/* Group Header */}
                        <button
                          onClick={() => toggleGroup(group.costCenterId)}
                          className="w-full sticky top-0 bg-retri-blue px-4 py-2 rounded-t-lg flex items-center justify-between"
                        >
                          <div className="text-left">
                            <h3 className="font-semibold text-white text-sm">
                              {generateGroupName(group, operator?.role)}
                            </h3>
                            <p className="text-xs text-blue-100">
                              {group.hourmeters.length} horometro{group.hourmeters.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          {isCollapsed
                            ? <ChevronDown className="h-4 w-4 text-white flex-shrink-0" />
                            : <ChevronUp className="h-4 w-4 text-white flex-shrink-0" />
                          }
                        </button>
                        
                        {/* Hourmeters in this group */}
                        {!isCollapsed && group.hourmeters.map((hourmeter) => (
                        <div key={hourmeter.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="space-y-3">
                          {/* Header with Date and Hours */}
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(hourmeter.date)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {getHoursWorked(hourmeter)} horas trabajadas
                              </div>
                            </div>
                          </div>

                          {/* Status Badges */}
                          <div className="flex flex-col gap-2">
                            {hourmeter.isSigned ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-retri-green text-white w-fit">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Residente: Firmado
                              </span>
                            ) : hourmeter.isRejected ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-retri-red text-white w-fit">
                                <XCircle className="w-3 h-3 mr-1" />
                                Residente: Rechazado
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-retri-orange text-white w-fit">
                                <Clock className="w-3 h-3 mr-1" />
                                Residente: Pendiente
                              </span>
                            )}
                          </div>

                        {/* Horómetros y Observaciones */}
                        <div className="mt-4 space-y-3 border-t border-gray-100 pt-3">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Horómetro Inicial
                              </div>
                              <div className="text-sm font-semibold text-gray-900 mt-1">
                                {hourmeter.initialHour || '0'}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Horómetro Final
                              </div>
                              <div className="text-sm font-semibold text-gray-900 mt-1">
                                {hourmeter.finalHour || '0'}
                              </div>
                            </div>
                            <div className="">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Horas trabajadas
                              </div>
                              <div className="text-sm font-semibold text-gray-900 mt-1">
                                {hourmeter.hoursWorked}
                              </div>
                            </div>
                            <div className="">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Horas accesorio
                              </div>
                              <div className="text-sm font-semibold text-gray-900 mt-1">
                                {hourmeter.hourmetersAccesories?.clientQuantity ?? 0}
                              </div>
                            </div>
                          </div>
                          {/* Observaciones */}
                          {hourmeter.observations && (
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Observaciones
                              </div>
                              <div className="text-sm text-gray-700 mt-1 bg-gray-50 rounded-lg p-3">
                                {hourmeter.observations}
                              </div>
                            </div>
                          )}
                        </div>

                          {/* Action Buttons - Mobile Optimized */}
                          <div className="grid grid-cols-1 gap-2 pt-2">
                            {hourmeter.isSigned ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="default"
                                  onClick={() => handleViewReceipt(hourmeter)}
                                  className="w-full justify-center h-12"
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver Recibo
                                </Button>
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    variant="outline"
                                    size="default"
                                    onClick={() => handleViewPDF(hourmeter)}
                                    className="justify-center h-12"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    PDF
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="default"
                                    onClick={() => handleDownloadPDF(hourmeter)}
                                    className="justify-center h-12"
                                  >
                                    <FileDown className="w-4 h-4 mr-1" />
                                    Descargar
                                  </Button>
                                </div>
                              </>
                            ) : hourmeter.isRejected ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="default"
                                  onClick={() => handleViewReceipt(hourmeter)}
                                  className="w-full justify-center h-12"
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver Rechazo
                                </Button>
                                {operator?.role !== 'supervisor' && operator?.role !== 'resident' && (
                                  <Button
                                    variant="secondary"
                                    size="default"
                                    onClick={() => handleEditHourmeter(hourmeter)}
                                    className="w-full justify-center h-12"
                                  >
                                    <Settings className="w-4 h-4 mr-2" />
                                    Corregir
                                  </Button>
                                )}
                                <Button
                                  variant="primary"
                                  size="default"
                                  onClick={() => handleSignReceipt(hourmeter)}
                                  className="w-full justify-center h-12"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Re-firmar
                                </Button>
                              </>
                            ) : (
                              <>
                                {operator?.role !== 'supervisor' && (
                                  <Button
                                    variant="primary"
                                    size="default"
                                    onClick={() => handleSignReceipt(hourmeter)}
                                    className="w-full justify-center h-12"
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    {operator?.role === 'resident' ? 'Firmar' : 'Residente Firma'}
                                  </Button>
                                )}
                                {operator?.role !== 'supervisor' && operator?.role !== 'resident' && (hourmeter.residentId || group.residentId) && isOlderThanOneDay(hourmeter.date) && (
                                  <Button
                                    variant="outline"
                                    size="default"
                                    onClick={() => handleSendReminder(hourmeter, group.residentId)}
                                    disabled={sendingReminder === hourmeter.id}
                                    className="w-full justify-center h-12 border-retri-orange text-retri-orange hover:bg-retri-orange/10"
                                  >
                                    <Bell className="w-4 h-4 mr-2" />
                                    {sendingReminder === hourmeter.id ? 'Enviando...' : 'Enviar Recordatorio'}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="default"
                                  onClick={() => handleViewPDF(hourmeter)}
                                  className="w-full justify-center h-12"
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  {operator?.role === 'supervisor' ? 'Ver Horometro' : 'Ver Preview'}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                        ))}
                      </div>
                      );
                    })}
                  </div>
                </>
              )}

          {/* Desktop Table - Full Width Outside Container */}
          {filteredGroups.length > 0 && (
            <div className="hidden md:block -mx-3 sm:-mx-4 lg:-mx-8">
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Horometros Registrados
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fecha
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Horómetro Inicial
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Horómetro Final
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Horas Trabajadas
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Horas accesorio
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Observaciones
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estado Firma Residente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredGroups.map((group) => {
                          const isCollapsed = collapsedGroups.has(group.costCenterId);
                          return (
                          <>
                            {/* Group Header Row */}
                            <tr
                              key={`group-${group.costCenterId}`}
                              className="bg-retri-blue cursor-pointer hover:bg-retri-bluedark"
                              onClick={() => toggleGroup(group.costCenterId)}
                            >
                              <td colSpan={8} className="px-6 py-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="text-sm font-semibold text-white">
                                      {generateGroupName(group, operator?.role)}
                                    </h3>
                                    <p className="text-xs text-blue-100">
                                      {group.hourmeters.length} horometro{group.hourmeters.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                  {isCollapsed
                                    ? <ChevronDown className="h-4 w-4 text-white" />
                                    : <ChevronUp className="h-4 w-4 text-white" />
                                  }
                                </div>
                              </td>
                            </tr>

                            {/* Hourmeters in this group */}
                            {!isCollapsed && group.hourmeters.map((hourmeter) => (
                          <tr key={hourmeter.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(hourmeter.date)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {hourmeter.initialHour || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {hourmeter.finalHour || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatHours(getHoursWorked(hourmeter))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {hourmeter.hourmetersAccesories?.clientQuantity ?? 0}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {hourmeter.observations ? (
                                <div className="max-w-md">
                                  <div className="break-words" title={hourmeter.observations}>
                                    {hourmeter.observations}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">Sin observaciones</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {hourmeter.isSigned ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-retri-green text-white">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Firmado
                                </span>
                              ) : hourmeter.isRejected ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-retri-red text-white">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Rechazado
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-retri-orange text-white">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pendiente
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                {hourmeter.isSigned ? (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewReceipt(hourmeter)}
                                      title="Ver detalles del recibo"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      Ver
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewPDF(hourmeter)}
                                      title="Ver PDF en nueva pestaña"
                                    >
                                      <ExternalLink className="w-4 h-4 mr-1" />
                                      PDF
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownloadPDF(hourmeter)}
                                      title="Descargar PDF"
                                    >
                                      <FileDown className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : hourmeter.isRejected ? (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewReceipt(hourmeter)}
                                      title="Ver detalles y comentarios del rechazo"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      Ver Rechazo
                                    </Button>
                                    {operator?.role !== 'supervisor' && operator?.role !== 'resident' && (
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleEditHourmeter(hourmeter)}
                                        title="Corregir horometro rechazado"
                                      >
                                        <Settings className="w-4 h-4 mr-1" />
                                        Corregir
                                      </Button>
                                    )}
                                    {operator?.role !== 'supervisor' && (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleSignReceipt(hourmeter)}
                                        title={operator?.role === 'resident' ? 'Firmar nuevamente' : 'Residente firma nuevamente'}
                                      >
                                        <Edit className="w-4 h-4 mr-1" />
                                        Re-firmar
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {operator?.role !== 'supervisor' && (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleSignReceipt(hourmeter)}
                                        title={operator?.role === 'resident' ? 'Firmar' : 'Residente firma'}
                                      >
                                        <Edit className="w-4 h-4 mr-1" />
                                        {operator?.role === 'resident' ? 'Firmar' : 'Residente firma'}
                                      </Button>
                                    )}
                                    {operator?.role !== 'supervisor' && operator?.role !== 'resident' && (hourmeter.residentId || group.residentId) && isOlderThanOneDay(hourmeter.date) && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleSendReminder(hourmeter, group.residentId)}
                                        disabled={sendingReminder === hourmeter.id}
                                        title="Enviar recordatorio al residente"
                                        className="border-retri-orange text-retri-orange hover:bg-retri-orange/10"
                                      >
                                        <Bell className="w-4 h-4 mr-1" />
                                        {sendingReminder === hourmeter.id ? 'Enviando...' : 'Recordatorio'}
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewPDF(hourmeter)}
                                      title="Ver preview del PDF"
                                    >
                                      <ExternalLink className="w-4 h-4 mr-1" />
                                      {operator?.role === 'supervisor' ? 'Ver' : 'Preview'}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                            ))}
                          </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
        </div>
      </main>

      {/* Reminder Modal */}
      {reminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 relative">
            <button
              onClick={() => setReminderModal(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center space-y-3">
              {reminderModal.type === 'success' ? (
                <div className="w-12 h-12 rounded-full bg-retri-green/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-retri-green" />
                </div>
              ) : reminderModal.type === 'already_sent' ? (
                <div className="w-12 h-12 rounded-full bg-retri-orange/10 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-retri-orange" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-retri-red/10 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-retri-red" />
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {reminderModal.type === 'success'
                  ? 'Recordatorio enviado'
                  : reminderModal.type === 'already_sent'
                    ? 'Recordatorio ya enviado'
                    : 'Error'}
              </h3>
              <p className="text-sm text-gray-600">
                {reminderModal.message}
              </p>
              <Button
                variant={reminderModal.type === 'success' ? 'primary' : 'outline'}
                size="default"
                onClick={() => setReminderModal(null)}
                className="w-full justify-center mt-2"
              >
                Entendido
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
